/**
 * Generic authenticated query hook.
 * Wraps TanStack Query's useQuery with Clerk auth token extraction.
 *
 * Usage:
 *   useApiQuery({ queryKey: ['items'], queryFn: (token) => api.list(token) })
 *   useApiQuery({ queryKey: ['item', id], queryFn: (token) => api.get(token, id), staleTime: 60000 })
 */
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import type { Query, QueryKey, UseQueryResult } from '@tanstack/react-query';
import { useAuth } from '../lib/auth';
import { shouldRetryApiQuery } from '../lib/api/retryPolicy';
import { log } from '@/lib/logger';

log.info('[useApiQuery] Module loaded');

export interface UseApiQueryOptions<T> {
  /** TanStack Query cache key */
  queryKey: QueryKey;
  /** Receives the auth token (guaranteed non-null) and returns data */
  queryFn: (token: string) => Promise<T>;
  /** Extra enabled condition ANDed with isSignedIn (default: true) */
  enabled?: boolean;
  /** Time in ms that data is considered fresh (passed through to useQuery) */
  staleTime?: number | ((query: Query<T, Error, T, QueryKey>) => number);
  /** Refetch interval in ms, or a function that returns ms/false (passed through to useQuery) */
  refetchInterval?: number | false | ((query: Query<T, Error, T, QueryKey>) => number | false);
  /** Keep previous data visible while new query key loads (default: false) */
  placeholderPreviousData?: boolean;
}

export function useApiQuery<T>(
  options: UseApiQueryOptions<T>,
): UseQueryResult<T, Error> {
  const { getToken, isSignedIn } = useAuth();

  return useQuery<T, Error>({
    queryKey: options.queryKey,
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return options.queryFn(token);
    },
    enabled: isSignedIn && (options.enabled ?? true),
    // Never retry a deterministic 4xx (e.g. a deleted project's 404) — it only
    // delays error handling and doubles the doomed request. Transient network /
    // 5xx failures still get one retry. See lib/api/retryPolicy.ts.
    retry: shouldRetryApiQuery,
    ...(options.staleTime !== undefined && { staleTime: options.staleTime }),
    ...(options.refetchInterval !== undefined && { refetchInterval: options.refetchInterval }),
    ...(options.placeholderPreviousData && { placeholderData: keepPreviousData }),
  });
}
